'use client';

import React, {
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import type { Swiper as SwiperCore } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ButtonSelectContext } from '@/common/contexts/button-select-context';
import 'swiper/css';
import ButtonSelectItem from './button-select-item';

interface ButtonSelectProps {
  children: ReactNode;
  className?: string;
  defaultChecked?: string;
  onCheck?: (value: string) => void;
  secondFeature: boolean;
  setSelected?: (value: string, secondFeature: boolean) => void;
  variant: Record<string, string | null>;
  features: string[];
}

const ButtonSelect = ({
  children,
  className,
  defaultChecked = '',
  onCheck,
  secondFeature,
  setSelected,
  variant,
  features,
}: ButtonSelectProps): ReactElement => {
  const [checked, setChecked] = useState<string>(defaultChecked);
  const [showNav, setShowNav] = useState<boolean>(false);

  const swiperRef = useRef<SwiperCore | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleClick = (value: string) => {
    if (!secondFeature) setChecked(value);
    onCheck?.(value);
  };

  const handleSelectFeature = (value: string) => {
    setSelected?.(value, secondFeature);
  };

  useEffect(() => {
    if (features.length > 0) {
      const newValue = variant[features[0]] ?? defaultChecked;
      setChecked(newValue);
    }
  }, [variant, features, defaultChecked]);

  useEffect(() => {
    const checkOverflow = () => {
      const container = containerRef.current;
      if (!container) return;

      const swiperWrapper = container?.querySelector('.swiper-wrapper');

      if (!swiperWrapper) return;

      setShowNav(container.offsetWidth < swiperWrapper.scrollWidth);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [React.Children.toArray(children).length]);

  return (
    <ButtonSelectContext.Provider
      value={{
        checked,
        onClick: handleClick,
        secondFeature,
        selectFeature: handleSelectFeature,
        variant,
        features,
      }}
    >
      <div
        ref={containerRef}
        className={classNames(className, 'relative w-full overflow-hidden')}
      >
        {showNav && (
          <>
            <NavButton
              direction="next"
              onClick={() => swiperRef.current?.slideNext()}
            />
          </>
        )}

        <Swiper
          dir="rtl"
          spaceBetween={8}
          slidesPerView="auto"
          style={{ width: '100%' }}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
        >
          {React.Children.map(children, (child, idx) => (
            <SwiperSlide key={idx} style={{ width: 'auto' }}>
              {child}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </ButtonSelectContext.Provider>
  );
};

const NavButton = ({
  direction,
  onClick,
}: {
  direction: 'prev' | 'next';
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-label={direction === 'prev' ? 'قبلی' : 'بعدی'}
    className={classNames(
      'absolute top-1/2 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full transition-all active:scale-90',
      direction === 'prev' ? 'right-0 rotate-180' : 'left-0'
    )}
    onClick={onClick}
  >
    <Image
      className="h-5 w-2 font-bold"
      src="/assets/images/svg/arrow-left-gray.svg"
      alt=""
      width={10}
      height={10}
    />
  </button>
);

ButtonSelect.Item = ButtonSelectItem;

export default ButtonSelect;
